import { useState, useEffect } from "react";
import { Users, Package, MapPin, Truck, Bluetooth, CurrencyEur, ChartBar, Trash, PencilSimple, ShieldCheck, CaretDown, WarningCircle, Pulse, Crown, Bell, Car, MapTrifold, Prohibit, ChatCircleDots, X, Lock, CheckCircle } from "@phosphor-icons/react";
import axios from "axios";
import OperationsCenter from "@/components/OperationsCenter";
import AdminChatModeration from "@/components/AdminChatModeration";
import ShipmentChat from "@/components/ShipmentChat";
import PodViewerModal from "@/components/PodViewerModal";
import "@/components/OperationsCenter.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLE_LABELS = { super_admin: "Super Admin", admin: "Admin", user: "Utilizador", intermediary: "Intermediario", driver: "Motorista" };
const ROLE_COLORS = { super_admin: "#dc2626", admin: "#7c3aed", user: "#2563eb", intermediary: "#d97706", driver: "#059669" };

const STATUS_LABELS = {
  awaiting_pickup: "A aguardar recolha", in_transit: "Em transito", delivered: "Entregue",
  awaiting_payment: "A aguardar pagamento", at_delivery_point: "No ponto de entrega",
  available: "Disponivel", at_collection_point: "No ponto",
};

function StatBox({ icon: Icon, value, label, color }) {
  return (
    <div className="bento-item p-5">
      <div className="flex items-center gap-3">
        <div style={{ width: 40, height: 40, borderRadius: 12, background: `${color}15`, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon size={20} weight="duotone" style={{ color }} />
        </div>
        <div>
          <span style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.4rem", display: "block", lineHeight: 1 }}>{value}</span>
          <span style={{ fontSize: "0.7rem", color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</span>
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard({ userRole = "admin" }) {
  const isSuperAdmin = userRole === "super_admin";
  const [activeTab, setActiveTab] = useState(isSuperAdmin ? "monitor" : "overview");
  const [showOpsCenter, setShowOpsCenter] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [users, setUsers] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [chatModalTc, setChatModalTc] = useState(null);  // tracking_code or null
  const [podViewTc, setPodViewTc] = useState(null);  // tracking_code or null
  const [monitor, setMonitor] = useState(null);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem("blumove_token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = async () => {
    try {
      const promises = [
        axios.get(`${API}/admin/dashboard`, { headers }),
        axios.get(`${API}/admin/users`, { headers }),
        axios.get(`${API}/admin/shipments`, { headers }),
      ];
      if (isSuperAdmin) promises.push(axios.get(`${API}/admin/monitor`, { headers }));
      const results = await Promise.all(promises);
      setDashboard(results[0].data);
      setUsers(results[1].data);
      setShipments(results[2].data);
      if (isSuperAdmin && results[3]) setMonitor(results[3].data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const refreshMonitor = async () => {
    if (!isSuperAdmin) return;
    try {
      const res = await axios.get(`${API}/admin/monitor`, { headers });
      setMonitor(res.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { fetchAll(); }, []);

  // Auto-refresh monitor every 15s when on monitor tab
  useEffect(() => {
    if (!isSuperAdmin || activeTab !== "monitor") return;
    const iv = setInterval(refreshMonitor, 15000);
    return () => clearInterval(iv);
  }, [activeTab, isSuperAdmin]);

  const deleteUser = async (userId) => {
    if (!window.confirm("Tem a certeza que quer eliminar este utilizador?")) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { headers });
      setUsers(users.filter(u => u._id !== userId));
    } catch (e) { console.error(e); }
  };

  const updateRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/role`, { role: newRole }, { headers });
      setUsers(users.map(u => u._id === userId ? { ...u, role: newRole } : u));
    } catch (e) { console.error(e); }
  };

  const updateShipmentStatus = async (trackingCode, newStatus) => {
    try {
      await axios.put(`${API}/admin/shipments/${trackingCode}/status`, { status: newStatus }, { headers });
      await fetchAll();
    } catch (e) { console.error(e); }
  };

  const simulateTamperAttempt = async (bagCode, trackingCode) => {
    if (!bagCode) return;
    try {
      const res = await axios.post(`${API}/beacons/${bagCode}/tamper-attempt`,
        { shock_g: 2.4, context: "admin_simulated_attempt" });
      const q = res.data?.auto_quarantined_now;
      const recent = res.data?.recent_count;
      const msg = q
        ? `Tentativa registada · ${recent} em 60min · BOLSA EM QUARENTENA`
        : `Tentativa registada (${recent} em 60min) em ${bagCode}`;
      window.alert(msg);
    } catch (e) {
      console.error(e);
      window.alert(`Erro ao simular tentativa em ${bagCode}`);
    }
  };

  if (loading) return <div className="pt-20 px-6 text-center"><p style={{ color: "var(--text-muted)" }}>A carregar painel admin...</p></div>;

  const tabs = [
    ...(isSuperAdmin ? [{ id: "monitor", label: "Monitorizacao Global", icon: Pulse }] : []),
    { id: "overview", label: "Visao geral", icon: ChartBar },
    { id: "users", label: "Utilizadores", icon: Users },
    { id: "shipments", label: "Envios", icon: Package },
    { id: "moderation", label: "Moderacao", icon: Prohibit },
  ];

  return (
    <div data-testid="admin-dashboard" className="pt-20 px-6 pb-12">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6 animate-fade-in-up">
          <div>
            <div className="flex items-center gap-2 mb-1">
              {isSuperAdmin ? (
                <>
                  <Crown size={20} weight="fill" style={{ color: "#dc2626" }} />
                  <span style={{ fontSize: "0.7rem", fontWeight: 700, color: "#dc2626", textTransform: "uppercase", letterSpacing: "0.1em" }}>Super Admin · Acesso Total</span>
                </>
              ) : (
                <>
                  <ShieldCheck size={20} weight="fill" style={{ color: "#7c3aed" }} />
                  <span style={{ fontSize: "0.7rem", fontWeight: 700, color: "#7c3aed", textTransform: "uppercase", letterSpacing: "0.1em" }}>Administracao</span>
                </>
              )}
            </div>
            <h1 style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.6rem" }}>
              {isSuperAdmin ? "Centro de operacoes" : "Painel de controlo"}
            </h1>
          </div>
          {isSuperAdmin && monitor && (
            <div className="flex items-center gap-2">
              <button
                data-testid="open-ops-center-btn"
                onClick={() => setShowOpsCenter(true)}
                style={{ padding: "8px 16px", borderRadius: 9999, border: "none", background: "linear-gradient(135deg, #dc2626, #b91c1c)", color: "white", fontWeight: 700, fontSize: "0.75rem", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, boxShadow: "0 4px 14px rgba(220,38,38,0.35)" }}
              >
                <MapTrifold size={14} weight="fill" /> Operações ao Vivo
              </button>
              <button
                data-testid="refresh-monitor-btn"
                onClick={refreshMonitor}
                style={{ padding: "8px 14px", borderRadius: 9999, border: "1px solid rgba(220,38,38,0.3)", background: "rgba(220,38,38,0.06)", color: "#dc2626", fontWeight: 600, fontSize: "0.75rem", cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}
              >
                <Pulse size={14} weight="fill" /> Atualizar ({new Date(monitor.timestamp).toLocaleTimeString("pt-PT")})
              </button>
            </div>
          )}
        </div>

        {showOpsCenter && <OperationsCenter onClose={() => setShowOpsCenter(false)} userRole={userRole} />}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 animate-fade-in-up stagger-1" data-testid="admin-tabs">
          {tabs.map(t => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setActiveTab(t.id)}
              className="flex items-center gap-2"
              style={{
                padding: "8px 16px", borderRadius: 9999, border: "none",
                background: activeTab === t.id ? "var(--primary)" : "var(--surface)",
                color: activeTab === t.id ? "white" : "var(--text-muted)",
                fontFamily: "Outfit", fontWeight: 600, fontSize: "0.8rem", cursor: "pointer",
                boxShadow: activeTab !== t.id ? "inset 0 0 0 1px var(--border-color)" : "none",
              }}
            >
              <t.icon size={14} weight={activeTab === t.id ? "fill" : "regular"} /> {t.label}
            </button>
          ))}
        </div>

        {/* Super Admin - Monitorizacao Global */}
        {activeTab === "monitor" && isSuperAdmin && monitor && (
          <div className="animate-fade-in" data-testid="super-admin-monitor">
            {/* Alerts banner */}
            {monitor.alerts.length > 0 && (
              <div className="bento-item p-4 mb-4" style={{ borderLeft: "4px solid #dc2626" }}>
                <div className="flex items-center gap-2 mb-2">
                  <WarningCircle size={16} weight="fill" style={{ color: "#dc2626" }} />
                  <span style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "0.85rem", color: "#dc2626" }}>Alertas do sistema ({monitor.alerts.length})</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {monitor.alerts.map((a, i) => (
                    <span key={i} data-testid={`alert-${a.type}`} style={{
                      padding: "4px 10px", borderRadius: 9999, fontSize: "0.72rem", fontWeight: 600,
                      background: a.severity === "alert" ? "rgba(220,38,38,0.1)" : a.severity === "warning" ? "rgba(217,119,6,0.1)" : "rgba(37,99,235,0.1)",
                      color: a.severity === "alert" ? "#dc2626" : a.severity === "warning" ? "#d97706" : "#2563eb"
                    }}>{a.message}</span>
                  ))}
                </div>
              </div>
            )}

            {/* KPI grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <StatBox icon={Package} value={monitor.shipments.total} label="Envios totais" color="var(--primary)" />
              <StatBox icon={Pulse} value={monitor.shipments.active.length} label="Envios ativos" color="#2563eb" />
              <StatBox icon={Truck} value={`${monitor.drivers.on_route}/${monitor.drivers.total}`} label="Motoristas a caminho" color="var(--success)" />
              <StatBox icon={Car} value={monitor.fleet.total_vehicles} label="Veiculos da frota" color="#d97706" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <StatBox icon={Bluetooth} value={`${monitor.beacons.online}/${monitor.beacons.total}`} label="Beacons online" color="#2563eb" />
              <StatBox icon={WarningCircle} value={monitor.beacons.low_battery} label="Bateria baixa" color="var(--error)" />
              <StatBox icon={CurrencyEur} value={`${monitor.financial.total_revenue.toFixed(2)}`} label="Receita total" color="var(--success)" />
              <StatBox icon={Users} value={monitor.users.total} label="Utilizadores" color="#7c3aed" />
            </div>

            {/* Two columns: Active shipments + Fleet */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
              <div className="bento-item p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 style={{ fontFamily: "Outfit", fontWeight: 600, fontSize: "0.9rem" }}>Envios ativos ({monitor.shipments.active.length})</h3>
                  <span style={{ fontSize: "0.7rem", color: "var(--text-muted)" }}>em tempo real</span>
                </div>
                <div style={{ maxHeight: 280, overflowY: "auto" }}>
                  {monitor.shipments.active.slice(0, 10).map(s => (
                    <div key={s.tracking_code} className="flex items-center justify-between" style={{ padding: "8px 0", borderBottom: "1px solid var(--border-color)" }}>
                      <div>
                        <div style={{ fontFamily: "JetBrains Mono", fontWeight: 600, fontSize: "0.78rem", color: "var(--primary)" }}>{s.tracking_code}</div>
                        <div style={{ fontSize: "0.72rem", color: "var(--text-muted)" }}>{s.pickup_point} → {s.delivery_point}</div>
                      </div>
                      <span style={{ padding: "3px 8px", borderRadius: 9999, background: "var(--surface)", fontSize: "0.68rem", fontWeight: 600, color: "var(--text-main)" }}>
                        {STATUS_LABELS[s.status] || s.status}
                      </span>
                    </div>
                  ))}
                  {monitor.shipments.active.length === 0 && <p style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>Sem envios ativos no momento.</p>}
                </div>
              </div>

              <div className="bento-item p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 style={{ fontFamily: "Outfit", fontWeight: 600, fontSize: "0.9rem" }}>Frota ({monitor.fleet.total_vehicles})</h3>
                  <span style={{ fontSize: "0.7rem", color: "var(--text-muted)" }}>{monitor.fleet.total_companies} empresas</span>
                </div>
                <div style={{ maxHeight: 280, overflowY: "auto" }}>
                  {monitor.fleet.vehicles.map(v => (
                    <div key={v.plate} className="flex items-center justify-between" style={{ padding: "8px 0", borderBottom: "1px solid var(--border-color)" }}>
                      <div className="flex items-center gap-2">
                        <Car size={16} weight="duotone" style={{ color: v.status === "active" ? "var(--success)" : v.status === "en_route" ? "#d97706" : "var(--text-muted)" }} />
                        <div>
                          <div style={{ fontFamily: "JetBrains Mono", fontWeight: 600, fontSize: "0.78rem" }}>{v.plate}</div>
                          <div style={{ fontSize: "0.7rem", color: "var(--text-muted)", textTransform: "capitalize" }}>{v.vehicle_type} · {v.bags_on_board.length} bolsa(s)</div>
                        </div>
                      </div>
                      <span style={{ padding: "3px 8px", borderRadius: 9999, background: v.status === "active" ? "rgba(5,150,105,0.1)" : "rgba(217,119,6,0.1)", color: v.status === "active" ? "var(--success)" : "#d97706", fontSize: "0.66rem", fontWeight: 700, textTransform: "uppercase" }}>
                        {v.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Three columns: Top points + Beacons offline + Users */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
              <div className="bento-item p-5">
                <h3 style={{ fontFamily: "Outfit", fontWeight: 600, fontSize: "0.9rem", marginBottom: 12 }}>Pontos com maior carga</h3>
                {monitor.top_points.slice(0, 6).map(p => {
                  const total = p.active_bags + p.pending_pickups + p.pending_deliveries;
                  return (
                    <div key={p.name} className="flex items-center justify-between" style={{ padding: "6px 0" }}>
                      <div>
                        <div style={{ fontSize: "0.78rem", fontWeight: 600 }}>{p.name}</div>
                        <div style={{ fontSize: "0.68rem", color: "var(--text-muted)" }}>{p.city}</div>
                      </div>
                      <span style={{ fontFamily: "JetBrains Mono", fontWeight: 700, fontSize: "0.85rem", color: total > 5 ? "#d97706" : "var(--text-main)" }}>{total}</span>
                    </div>
                  );
                })}
              </div>

              <div className="bento-item p-5">
                <h3 style={{ fontFamily: "Outfit", fontWeight: 600, fontSize: "0.9rem", marginBottom: 12 }}>Beacons offline</h3>
                {monitor.beacons.offline_list.length === 0 ? (
                  <p style={{ fontSize: "0.78rem", color: "var(--success)" }}>Todos online ✓</p>
                ) : (
                  monitor.beacons.offline_list.map(b => (
                    <div key={b.bag_code} className="flex items-center justify-between" style={{ padding: "6px 0" }}>
                      <div>
                        <div style={{ fontFamily: "JetBrains Mono", fontWeight: 600, fontSize: "0.78rem" }}>{b.bag_code}</div>
                        <div style={{ fontSize: "0.68rem", color: "var(--text-muted)" }}>Bat: {b.battery_level}%</div>
                      </div>
                      <WarningCircle size={16} weight="fill" style={{ color: "var(--error)" }} />
                    </div>
                  ))
                )}
              </div>

              <div className="bento-item p-5">
                <h3 style={{ fontFamily: "Outfit", fontWeight: 600, fontSize: "0.9rem", marginBottom: 12 }}>Utilizadores por tipo</h3>
                {Object.entries(monitor.users.by_role).filter(([_, c]) => c > 0).map(([role, count]) => (
                  <div key={role} className="flex items-center justify-between" style={{ padding: "6px 0" }}>
                    <div className="flex items-center gap-2">
                      <div style={{ width: 8, height: 8, borderRadius: "50%", background: ROLE_COLORS[role] || "var(--text-muted)" }} />
                      <span style={{ fontSize: "0.8rem" }}>{ROLE_LABELS[role] || role}</span>
                    </div>
                    <span style={{ fontFamily: "JetBrains Mono", fontWeight: 600, fontSize: "0.85rem" }}>{count}</span>
                  </div>
                ))}
                {monitor.users.locked_accounts > 0 && (
                  <div style={{ marginTop: 8, padding: 8, borderRadius: 8, background: "rgba(220,38,38,0.08)", fontSize: "0.7rem", color: "#dc2626", fontWeight: 600 }}>
                    ⚠ {monitor.users.locked_accounts} conta(s) bloqueada(s)
                  </div>
                )}
              </div>
            </div>

            {/* Recent notifications feed */}
            <div className="bento-item p-5">
              <div className="flex items-center gap-2 mb-3">
                <Bell size={16} weight="fill" style={{ color: "var(--primary)" }} />
                <h3 style={{ fontFamily: "Outfit", fontWeight: 600, fontSize: "0.9rem" }}>Feed de notificacoes do sistema</h3>
              </div>
              <div style={{ maxHeight: 240, overflowY: "auto" }}>
                {monitor.recent_notifications.map((n, i) => (
                  <div key={i} style={{ padding: "8px 0", borderBottom: "1px solid var(--border-color)" }}>
                    <div className="flex items-center justify-between">
                      <span style={{ fontSize: "0.8rem", fontWeight: 600 }}>{n.title}</span>
                      <span style={{ fontSize: "0.65rem", color: "var(--text-muted)" }}>
                        {n.created_at ? new Date(n.created_at).toLocaleTimeString("pt-PT") : ""}
                      </span>
                    </div>
                    <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", marginTop: 2 }}>{n.message}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Overview */}
        {activeTab === "overview" && dashboard && (
          <div className="animate-fade-in">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <StatBox icon={Users} value={dashboard.total_users} label="Utilizadores" color="#2563eb" />
              <StatBox icon={Package} value={dashboard.total_shipments} label="Envios totais" color="var(--primary)" />
              <StatBox icon={MapPin} value={dashboard.total_points} label="Pontos" color="var(--accent)" />
              <StatBox icon={Truck} value={dashboard.total_drivers} label="Motoristas" color="var(--success)" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <StatBox icon={Bluetooth} value={dashboard.total_beacons} label="Beacons" color="#2563eb" />
              <StatBox icon={WarningCircle} value={dashboard.low_battery_beacons} label="Bateria baixa" color="var(--error)" />
              <StatBox icon={CurrencyEur} value={`${dashboard.total_revenue.toFixed(2)}`} label="Receita total" color="var(--success)" />
              <StatBox icon={Package} value={dashboard.total_bags} label="Bolsas" color="var(--warning)" />
            </div>

            {/* Breakdowns */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bento-item p-5">
                <h3 style={{ fontFamily: "Outfit", fontWeight: 600, fontSize: "0.9rem", marginBottom: 12 }}>Utilizadores por tipo</h3>
                {Object.entries(dashboard.users_by_role).map(([role, count]) => (
                  <div key={role} className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div style={{ width: 8, height: 8, borderRadius: "50%", background: ROLE_COLORS[role] || "var(--text-muted)" }} />
                      <span style={{ fontSize: "0.85rem" }}>{ROLE_LABELS[role] || role}</span>
                    </div>
                    <span style={{ fontFamily: "JetBrains Mono", fontWeight: 600, fontSize: "0.85rem" }}>{count}</span>
                  </div>
                ))}
              </div>
              <div className="bento-item p-5">
                <h3 style={{ fontFamily: "Outfit", fontWeight: 600, fontSize: "0.9rem", marginBottom: 12 }}>Envios por estado</h3>
                {Object.entries(dashboard.shipments_by_status).map(([status, count]) => (
                  <div key={status} className="flex items-center justify-between mb-2">
                    <span style={{ fontSize: "0.85rem" }}>{STATUS_LABELS[status] || status}</span>
                    <span style={{ fontFamily: "JetBrains Mono", fontWeight: 600, fontSize: "0.85rem" }}>{count}</span>
                  </div>
                ))}
              </div>
              <div className="bento-item p-5">
                <h3 style={{ fontFamily: "Outfit", fontWeight: 600, fontSize: "0.9rem", marginBottom: 12 }}>Bolsas por estado</h3>
                {Object.entries(dashboard.bags_by_status).map(([status, count]) => (
                  <div key={status} className="flex items-center justify-between mb-2">
                    <span style={{ fontSize: "0.85rem" }}>{STATUS_LABELS[status] || status}</span>
                    <span style={{ fontFamily: "JetBrains Mono", fontWeight: 600, fontSize: "0.85rem" }}>{count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Users */}
        {activeTab === "users" && (
          <div className="animate-fade-in">
            <div className="bento-item overflow-hidden">
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid var(--border-color)" }}>
                      <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "0.7rem", fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Nome</th>
                      <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "0.7rem", fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Email</th>
                      <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "0.7rem", fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Tipo</th>
                      <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "0.7rem", fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Criado</th>
                      <th style={{ padding: "12px 16px", textAlign: "right", fontSize: "0.7rem", fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Acoes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(u => (
                      <tr key={u._id} style={{ borderBottom: "1px solid var(--border-color)" }} data-testid={`user-row-${u.email}`}>
                        <td style={{ padding: "12px 16px", fontWeight: 600, fontSize: "0.85rem" }}>{u.name}</td>
                        <td style={{ padding: "12px 16px", fontSize: "0.85rem", color: "var(--text-muted)" }}>{u.email}</td>
                        <td style={{ padding: "12px 16px" }}>
                          <div style={{ position: "relative", display: "inline-block" }}>
                            <select
                              data-testid={`role-select-${u.email}`}
                              value={u.role}
                              onChange={e => updateRole(u._id, e.target.value)}
                              style={{ appearance: "none", padding: "4px 28px 4px 10px", borderRadius: 9999, border: "none", background: `${ROLE_COLORS[u.role] || "#666"}15`, color: ROLE_COLORS[u.role] || "#666", fontWeight: 600, fontSize: "0.75rem", cursor: "pointer" }}
                            >
                              {Object.entries(ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                            </select>
                            <CaretDown size={12} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: ROLE_COLORS[u.role] }} />
                          </div>
                        </td>
                        <td style={{ padding: "12px 16px", fontSize: "0.8rem", color: "var(--text-muted)" }}>
                          {u.created_at ? new Date(u.created_at).toLocaleDateString("pt-PT") : "---"}
                        </td>
                        <td style={{ padding: "12px 16px", textAlign: "right" }}>
                          <button
                            data-testid={`delete-user-${u.email}`}
                            onClick={() => deleteUser(u._id)}
                            style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", padding: 6, borderRadius: 8 }}
                            onMouseEnter={e => e.currentTarget.style.color = "var(--error)"}
                            onMouseLeave={e => e.currentTarget.style.color = "var(--text-muted)"}
                          >
                            <Trash size={16} weight="bold" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Shipments */}
        {activeTab === "shipments" && (
          <div className="animate-fade-in">
            <div className="bento-item overflow-hidden">
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid var(--border-color)" }}>
                      <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "0.7rem", fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Rastreio</th>
                      <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "0.7rem", fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Remetente</th>
                      <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "0.7rem", fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Destinatario</th>
                      <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "0.7rem", fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Bolsa</th>
                      <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "0.7rem", fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Estado</th>
                      <th style={{ padding: "12px 16px", textAlign: "center", fontSize: "0.7rem", fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>POD</th>
                      <th style={{ padding: "12px 16px", textAlign: "center", fontSize: "0.7rem", fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Acoes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shipments.map(s => (
                      <tr key={s.tracking_code} style={{ borderBottom: "1px solid var(--border-color)" }} data-testid={`admin-shipment-${s.tracking_code}`}>
                        <td style={{ padding: "12px 16px" }}>
                          <span style={{ fontFamily: "JetBrains Mono", fontWeight: 600, fontSize: "0.85rem", color: "var(--primary)" }}>{s.tracking_code}</span>
                        </td>
                        <td style={{ padding: "12px 16px", fontSize: "0.85rem" }}>{s.sender_name}</td>
                        <td style={{ padding: "12px 16px", fontSize: "0.85rem" }}>{s.recipient_name}</td>
                        <td style={{ padding: "12px 16px" }}>
                          <span style={{ fontFamily: "JetBrains Mono", fontSize: "0.8rem" }}>{s.bag_code}</span>
                        </td>
                        <td style={{ padding: "12px 16px" }}>
                          <div style={{ position: "relative", display: "inline-block" }}>
                            <select
                              data-testid={`status-select-${s.tracking_code}`}
                              value={s.status}
                              onChange={e => updateShipmentStatus(s.tracking_code, e.target.value)}
                              style={{ appearance: "none", padding: "4px 28px 4px 10px", borderRadius: 9999, border: "none", fontWeight: 600, fontSize: "0.75rem", cursor: "pointer" }}
                              className={`status-${s.status}`}
                            >
                              {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                            </select>
                            <CaretDown size={12} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} />
                          </div>
                        </td>
                        <td style={{ padding: "12px 16px", textAlign: "center" }}>
                          {(s.status === "delivered" || s.pod) ? (
                            <button
                              data-testid={`admin-view-pod-${s.tracking_code}`}
                              onClick={() => setPodViewTc(s.tracking_code)}
                              title="Ver prova de entrega"
                              className="flex items-center gap-1"
                              style={{ margin: "0 auto", padding: "4px 10px", borderRadius: 9999, background: "rgba(22,163,74,0.1)", border: "1px solid rgba(22,163,74,0.25)", cursor: "pointer", fontSize: "0.72rem", fontWeight: 700, color: "var(--success)" }}
                            >
                              <CheckCircle size={12} weight="fill" /> POD
                            </button>
                          ) : (
                            <span style={{ color: "var(--text-muted)", fontSize: "0.8rem" }}>—</span>
                          )}
                        </td>
                        <td style={{ padding: "12px 16px", textAlign: "center" }}>
                          <div className="flex items-center gap-2 justify-center">
                            <button
                              data-testid={`admin-open-chat-${s.tracking_code}`}
                              onClick={() => setChatModalTc(s.tracking_code)}
                              title="Abrir chat (entrar como moderador BluMove)"
                              className="flex items-center gap-1"
                              style={{ padding: "5px 10px", borderRadius: 9999, background: "var(--surface)", border: "1px solid var(--border-color)", cursor: "pointer", fontSize: "0.72rem", fontWeight: 600, color: "var(--primary)" }}
                            >
                              <ChatCircleDots size={12} weight="duotone" /> Chat
                            </button>
                            <button
                              data-testid={`admin-simulate-tamper-${s.tracking_code}`}
                              onClick={() => simulateTamperAttempt(s.bag_code, s.tracking_code)}
                              title="Simular tentativa de abertura (demo)"
                              className="flex items-center gap-1"
                              style={{ padding: "5px 10px", borderRadius: 9999, background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.25)", cursor: "pointer", fontSize: "0.72rem", fontWeight: 600, color: "#dc2626" }}
                            >
                              <Lock size={12} weight="duotone" /> Simular
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === "moderation" && <AdminChatModeration />}
      </div>

      {/* Admin chat modal — admin joins any shipment chat as "BluMove" moderator */}
      {chatModalTc && (
        <div
          data-testid="admin-chat-modal"
          onClick={() => setChatModalTc(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, backdropFilter: "blur(4px)" }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bento-item p-6"
            style={{ width: "min(560px, 92vw)", maxHeight: "85vh", overflowY: "auto" }}
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.1rem" }}>Chat moderado — {chatModalTc}</h3>
                <p style={{ fontSize: "0.78rem", color: "var(--text-muted)", marginTop: 2 }}>Entrou como <strong style={{ color: "var(--accent)" }}>BluMove (admin)</strong>. Pode eliminar mensagens e bloquear autores.</p>
              </div>
              <button
                data-testid="admin-chat-modal-close"
                onClick={() => setChatModalTc(null)}
                style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }}
              >
                <X size={20} />
              </button>
            </div>
            <ShipmentChat trackingCode={chatModalTc} />
          </div>
        </div>
      )}

      {podViewTc && (
        <PodViewerModal trackingCode={podViewTc} onClose={() => setPodViewTc(null)} />
      )}
    </div>
  );
}
